import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, TrendingUp, AlertTriangle, Zap, Brain, 
  Target, ArrowRight, CheckCircle2,
  Activity, Moon, Flame, Heart, ChevronRight, Lightbulb,
  BarChart3, LineChart, PieChart, RefreshCw, Info, X,
  Database, DollarSign, Focus, Dumbbell,
  Utensils, HeartPulse, ClipboardList
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { getAnalyticsSummary, getTrackingDaysCount, AnalyticsSummary, Pattern, Correlation, Anomaly, Prediction } from '../services/analyticsService';
import { useAuth } from '../contexts/AuthContext';

export default function InsightsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [daysCount, setDaysCount] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<'overview' | 'patterns' | 'correlations' | 'predictions'>('overview');
  const [showTutorial, setShowTutorial] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  useEffect(() => {
    // Check if user has seen tutorial
    const tutorialSeen = localStorage.getItem('insights_tutorial_seen');
    if (!tutorialSeen) {
      setShowTutorial(true);
    }
    loadData();
  }, []);

  const loadData = async (forceRefresh: boolean = false) => {
    setLoading(true);
    try {
      // Always fetch days count (lightweight, no credits)
      const days = await getTrackingDaysCount();
      setDaysCount(days);

      // Only fetch full summary if enough data
      if (days >= 2) {
        const summary = await getAnalyticsSummary(forceRefresh);
        setData(summary);
      } else {
        setData(null);
      }
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading analytics:', error);
      // Try to get days count even if summary fails
      try {
        const days = await getTrackingDaysCount();
        setDaysCount(days);
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  const handleManualRefresh = async () => {
    // Force refresh will invalidate cache and generate fresh AI recommendations
    if (window.confirm('🔄 Regenerarea analizelor folosește asistentul AI și va crea recomandări noi personalizate. Sigur vrei să actualizezi?')) {
      await loadData(true); // Pass true to force refresh
    }
  };

  const closeTutorial = () => {
    setShowTutorial(false);
    localStorage.setItem('insights_tutorial_seen', 'true');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 pb-20">
        <PageHeader title="AI Tracking" subtitle="Analiză inteligentă a datelor tale" />
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin" />
            <p className="text-gray-600 font-medium">Se analizează datele...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 pb-20">
        <PageHeader title="AI Tracking" subtitle="Analiză inteligentă a datelor tale" />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">

          {/* Main empty state card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header gradient */}
            <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 p-6 text-white text-center">
              <div className="inline-flex p-4 rounded-full bg-white/20 backdrop-blur-sm mb-4">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-2">
                Nu există încă suficiente date pentru analiză AI.
              </h3>
              <p className="text-indigo-100 text-sm leading-relaxed">
                Completează jurnalul zilnic 5–7 zile pentru a primi pattern-uri, corelații și predicții personalizate.
              </p>
            </div>

            {/* Progress indicator */}
            <div className="p-6 border-b border-gray-100">
              <ProgressIndicator daysCount={daysCount} />
            </div>

            {/* CTA Button */}
            <div className="p-6">
              <button
                onClick={() => navigate('/advanced-tracking')}
                className="w-full py-4 px-6 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl font-bold text-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3"
              >
                <ClipboardList className="w-6 h-6" />
                Completează jurnalul de azi
              </button>
            </div>
          </div>

          {/* Ce analizează AI-ul */}
          <WhatAIAnalyzes />

        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 pb-20">
      <PageHeader 
        title="AI Tracking" 
        subtitle="Analiză inteligentă a stării tale de sănătate"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tutorial Modal */}
        {showTutorial && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-2xl w-full max-h-[85vh] flex flex-col relative">
              {/* Fixed Header */}
              <div className="flex items-center gap-3 sm:gap-4 p-4 sm:p-6 pb-3 border-b border-gray-100 flex-shrink-0">
                <div className="p-3 bg-indigo-100 rounded-2xl">
                  <Info className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Bine ai venit la AI Tracking!</h2>
                  <p className="text-sm text-gray-600">Cum funcționează sistemul de analiză AI</p>
                </div>
                <button
                  onClick={closeTutorial}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="overflow-y-auto flex-1 p-4 sm:p-6 space-y-3 sm:space-y-4">
                <div className="p-3 sm:p-4 bg-blue-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Ce analizează AI-ul?</h3>
                  </div>
                  <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                    <li>• <strong>Pattern-uri:</strong> Corelații între somn, durere, stres</li>
                    <li>• <strong>Corelații:</strong> Relații statistice între metrici</li>
                    <li>• <strong>Anomalii:</strong> Deviații semnificative de la medie</li>
                    <li>• <strong>Predicții:</strong> Tendințe bazate pe istoric</li>
                  </ul>
                </div>

                <div className="p-3 sm:p-4 bg-amber-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <Database className="w-5 h-5 text-amber-600" />
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Date necesare:</h3>
                  </div>
                  <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                    <li>• Minim <strong>2-3 zile consecutive</strong> de tracking</li>
                    <li>• Logs în <strong>aceeași zi</strong> pentru corelații</li>
                    <li>• Mai multe zile = insights mai precise!</li>
                  </ul>
                </div>

                <div className="p-3 sm:p-4 bg-green-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Cost & Refresh:</h3>
                  </div>
                  <ul className="text-xs sm:text-sm text-gray-700 space-y-1">
                    <li>• Analizele se generează <strong>automat zilnic</strong></li>
                    <li>• Datele sunt cache-uite 24h</li>
                    <li>• Refresh manual: <strong>doar 1x pe zi</strong></li>
                    <li>• ⚠️ Următorul refresh: <strong>mâine</strong></li>
                  </ul>
                </div>
              </div>

              {/* Fixed Footer Button */}
              <div className="p-4 sm:p-6 pt-3 border-t border-gray-100 flex-shrink-0">
                <button
                  onClick={closeTutorial}
                  className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                >
                  Am înțeles!
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Hero Card - Mobile Optimized */}
        <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-2xl sm:rounded-3xl p-4 sm:p-8 mb-6 sm:mb-8 text-white shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full -ml-32 -mb-32 blur-3xl" />
          
          <div className="relative z-10">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 sm:p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Sparkles className="w-6 h-6 sm:w-8 sm:h-8" />
                </div>
                <h2 className="text-xl sm:text-3xl font-bold">Analytics Dashboard</h2>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowTutorial(true)}
                  className="p-2 sm:p-3 bg-white/20 hover:bg-white/30 rounded-xl backdrop-blur-sm transition-colors"
                  title="Cum funcționează"
                >
                  <Info className="w-5 h-5" />
                </button>
                <button
                  onClick={handleManualRefresh}
                  className="p-2 sm:px-4 sm:py-2 bg-white/20 hover:bg-white/30 rounded-xl backdrop-blur-sm transition-colors flex items-center gap-2"
                  title="Actualizează analize (1x pe zi)"
                >
                  <RefreshCw className="w-5 h-5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline text-sm">Refresh</span>
                </button>
              </div>
            </div>

            {/* Description - Mobile Friendly */}
            <p className="text-indigo-100 text-sm sm:text-lg leading-relaxed mb-2">
              <span className="hidden sm:inline">
                AI-ul a analizat ultimele 30 de zile de date și a detectat {data.patterns.length} pattern-uri,
                {' '}{data.correlations.length} corelații și {data.anomalies.length} anomalii.
              </span>
              <span className="sm:hidden">
                {data.patterns.length} pattern-uri • {data.correlations.length} corelații • {data.anomalies.length} anomalii
              </span>
            </p>
            
            {lastRefresh && (
              <p className="text-indigo-200 text-xs sm:text-sm mb-4 sm:mb-6">
                Update: {lastRefresh.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })} • Cache 24h
              </p>
            )}
            
            {/* Quick Stats - Mobile 2x2 Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4">
                  <div className="flex items-center gap-2 mb-1 sm:mb-2">
                    <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-200" />
                    <span className="text-indigo-200 text-xs sm:text-sm">Pattern-uri</span>
                  </div>
                  <p className="text-2xl sm:text-3xl font-bold">{data.patterns.length}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4">
                  <div className="flex items-center gap-2 mb-1 sm:mb-2">
                    <LineChart className="w-4 h-4 sm:w-5 sm:h-5 text-purple-200" />
                    <span className="text-purple-200 text-xs sm:text-sm">Corelații</span>
                  </div>
                  <p className="text-2xl sm:text-3xl font-bold">{data.correlations.length}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4">
                  <div className="flex items-center gap-2 mb-1 sm:mb-2">
                    <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-pink-200" />
                    <span className="text-pink-200 text-xs sm:text-sm">Anomalii</span>
                  </div>
                  <p className="text-2xl sm:text-3xl font-bold">{data.anomalies.length}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4">
                  <div className="flex items-center gap-2 mb-1 sm:mb-2">
                    <Target className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-200" />
                    <span className="text-yellow-200 text-xs sm:text-sm">Predicții</span>
                  </div>
                  <p className="text-2xl sm:text-3xl font-bold">{data.predictions.length}</p>
                </div>
            </div>
          </div>
        </div>

        {/* Health Goal Context Banner */}
        {user?.healthGoal && (() => {
          const goalContext: Record<string, { focus: string; metrics: string; tip: string }> = {
            // HEALTHY MODE
            WEIGHT_LOSS: { 
              focus: 'Slăbire Sănătoasă', 
              metrics: 'Somn (7-9h), Stres (scăzut), Calorii (deficit)', 
              tip: 'Monitorizează corelația somn-foame. Stresul cronic = cortizol = grăsime abdominală.' 
            },
            MUSCLE_GAIN: { 
              focus: 'Creștere Musculară', 
              metrics: 'Somn (8-9h recuperare), Proteine, Forță antrenament', 
              tip: 'Somnul = sinteză proteică. Pattern-uri: mai mult somn = mai multă forță în sală.' 
            },
            BETTER_SLEEP: { 
              focus: 'Optimizare Somn', 
              metrics: 'Durată somn, Calitate, Stress seara', 
              tip: 'Caută corelații: Screen time seara → somn prost. Magneziu → somn profund.' 
            },
            REDUCE_BELLY_FAT: { 
              focus: 'Reducere Grăsime', 
              metrics: 'Carbo (<100g), Somn, Cortizol/Stres', 
              tip: 'Stres ridicat = cortizol = depozit ventral. Somn prost = foame crescută.' 
            },
            
            // CHRONIC MODE
            REDUCE_JOINT_PAIN: { 
              focus: 'Control Durere Articulară', 
              metrics: 'Durere nivele, Inflamație (alimente), Mobilitate', 
              tip: 'Pattern-uri: alimente proinflamatorii → durere next day. Omega-3 → scădere durere.' 
            },
            STABILIZE_GLUCOSE: { 
              focus: 'Stabilizare Glicemie', 
              metrics: 'Glucose levels, Mese timing, Somn', 
              tip: 'Somn prost = rezistență insulină. Căută spike-uri după mese cu index glicemic ridicat.' 
            },
            CONTROL_CHRONIC_PAIN: { 
              focus: 'Management Durere Cronică', 
              metrics: 'Durere 0-10, Trigger foods, Sleep quality', 
              tip: 'Durere severă → somn fragmentat → mai multă durere (ciclu vicios). Break the cycle!' 
            }
          };

          const context = goalContext[user.healthGoal];
          if (!context) return null;

          return (
            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl p-5 mb-6 border-2 border-cyan-200 shadow-lg">
              <div className="flex items-start gap-3">
                <div className="p-3 bg-cyan-500 rounded-xl backdrop-blur-sm">
                  <Focus className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">🎯 Focus: {context.focus}</h3>
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>Metrics importante:</strong> {context.metrics}
                  </p>
                  <p className="text-xs text-cyan-700 italic">
                    💡 {context.tip}
                  </p>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-lg p-2 mb-8">
          <div className="grid grid-cols-4 gap-2">
            {[
              { id: 'overview', label: 'Rezumat', icon: PieChart },
              { id: 'patterns', label: 'Pattern-uri', icon: TrendingUp },
              { id: 'correlations', label: 'Corelații', icon: Zap },
              { id: 'predictions', label: 'Predicții', icon: Brain },
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`
                    flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all
                    ${isActive 
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg' 
                      : 'text-gray-600 hover:bg-gray-50'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        {activeTab === 'overview' && <OverviewTab data={data} />}
        {activeTab === 'patterns' && <PatternsTab patterns={data.patterns} />}
        {activeTab === 'correlations' && <CorrelationsTab correlations={data.correlations} />}
        {activeTab === 'predictions' && <PredictionsTab predictions={data.predictions} anomalies={data.anomalies} />}
      </div>
    </div>
  );
}

// ============================================
// PROGRESS INDICATOR COMPONENT
// ============================================

function ProgressIndicator({ daysCount }: { daysCount: number }) {
  const TARGET_DAYS = 7;
  const UNLOCK_DAYS = 5;
  const clampedDays = Math.min(daysCount, TARGET_DAYS);
  const progressPct = (clampedDays / TARGET_DAYS) * 100;

  const getStatusInfo = () => {
    if (daysCount === 0) {
      return {
        label: 'Începe jurnalul tău',
        color: 'text-gray-500',
        bgColor: 'bg-gray-100',
        barColor: 'from-gray-300 to-gray-400',
        emoji: '📋'
      };
    }
    if (daysCount < UNLOCK_DAYS) {
      return {
        label: `Mai ai ${UNLOCK_DAYS - daysCount} ${UNLOCK_DAYS - daysCount === 1 ? 'zi' : 'zile'} până la analiza AI`,
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        barColor: 'from-amber-400 to-orange-500',
        emoji: '🔥'
      };
    }
    if (daysCount < TARGET_DAYS) {
      return {
        label: 'Analiza devine disponibilă! Continuă pentru rezultate mai precise',
        color: 'text-indigo-600',
        bgColor: 'bg-indigo-50',
        barColor: 'from-indigo-500 to-purple-600',
        emoji: '🚀'
      };
    }
    return {
      label: 'Date complete! Analiza AI este disponibilă',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      barColor: 'from-green-500 to-emerald-600',
      emoji: '✅'
    };
  };

  const status = getStatusInfo();

  return (
    <div className={`rounded-xl p-4 ${status.bgColor}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xl">{status.emoji}</span>
          <span className="font-semibold text-gray-900 text-sm">Progres jurnal</span>
        </div>
        <span className={`text-lg font-bold ${status.color}`}>
          {clampedDays}/{TARGET_DAYS} zile
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-3 bg-white/70 rounded-full overflow-hidden mb-3 shadow-inner">
        <div
          className={`h-full bg-gradient-to-r ${status.barColor} rounded-full transition-all duration-700`}
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* Day dots */}
      <div className="flex gap-1.5 mb-3">
        {Array.from({ length: TARGET_DAYS }).map((_, i) => (
          <div
            key={i}
            className={`flex-1 h-2 rounded-full transition-all ${
              i < clampedDays
                ? `bg-gradient-to-r ${status.barColor}`
                : 'bg-white/60'
            }`}
          />
        ))}
      </div>

      <p className={`text-xs font-medium ${status.color}`}>{status.label}</p>

      {/* Milestone labels */}
      <div className="flex justify-between mt-2">
        <span className="text-xs text-gray-400">1 zi</span>
        <span className={`text-xs font-medium ${daysCount >= UNLOCK_DAYS ? 'text-indigo-600' : 'text-gray-400'}`}>
          5/7 – analiză disponibilă
        </span>
        <span className={`text-xs font-medium ${daysCount >= TARGET_DAYS ? 'text-green-600' : 'text-gray-400'}`}>
          7/7 ✓
        </span>
      </div>
    </div>
  );
}

// ============================================
// WHAT AI ANALYZES COMPONENT
// ============================================

function WhatAIAnalyzes() {
  const categories = [
    { icon: Moon, label: 'Somn', color: 'indigo', desc: 'Durată, calitate, pattern-uri nocturne' },
    { icon: AlertTriangle, label: 'Simptome', color: 'rose', desc: 'Frecvență, intensitate, evoluție' },
    { icon: Flame, label: 'Durere', color: 'red', desc: 'Nivel, locație, factori declanșatori' },
    { icon: Brain, label: 'Stres', color: 'orange', desc: 'Nivel, dispoziție, trigger-uri' },
    { icon: Utensils, label: 'Alimentație', color: 'green', desc: 'Mese, calorii, nutrienți' },
    { icon: Dumbbell, label: 'Activitate', color: 'blue', desc: 'Exerciții, mișcare, energie' },
    { icon: HeartPulse, label: 'Vitale', color: 'pink', desc: 'Puls, tensiune, glicemie' },
  ];

  const colorMap: Record<string, { bg: string; icon: string; dot: string }> = {
    indigo: { bg: 'bg-indigo-50', icon: 'text-indigo-600', dot: 'bg-indigo-500' },
    rose:   { bg: 'bg-rose-50',   icon: 'text-rose-600',   dot: 'bg-rose-500' },
    red:    { bg: 'bg-red-50',    icon: 'text-red-600',    dot: 'bg-red-500' },
    orange: { bg: 'bg-orange-50', icon: 'text-orange-600', dot: 'bg-orange-500' },
    green:  { bg: 'bg-green-50',  icon: 'text-green-600',  dot: 'bg-green-500' },
    blue:   { bg: 'bg-blue-50',   icon: 'text-blue-600',   dot: 'bg-blue-500' },
    pink:   { bg: 'bg-pink-50',   icon: 'text-pink-600',   dot: 'bg-pink-500' },
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="p-3 bg-purple-100 rounded-xl">
          <Sparkles className="w-6 h-6 text-purple-600" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">Ce analizează AI-ul</h3>
          <p className="text-sm text-gray-500">Datele colectate zilnic pentru analiză</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {categories.map(({ icon: Icon, label, color, desc }) => {
          const c = colorMap[color];
          return (
            <div key={label} className={`flex items-center gap-4 p-3 ${c.bg} rounded-xl`}>
              <div className={`p-2.5 bg-white rounded-xl shadow-sm`}>
                <Icon className={`w-5 h-5 ${c.icon}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm">{label}</p>
                <p className="text-xs text-gray-500 truncate">{desc}</p>
              </div>
              <div className={`w-2 h-2 rounded-full ${c.dot} flex-shrink-0`} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// OVERVIEW TAB
// ============================================

function OverviewTab({ data }: { data: AnalyticsSummary }) {
  return (
    <div className="space-y-6">
      {/* AI Recommendations */}
      <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-6 border-2 border-amber-200 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-amber-500 rounded-xl">
            <Lightbulb className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Recomandări AI</h3>
            <p className="text-sm text-amber-700">Generate automat bazat pe datele tale</p>
          </div>
        </div>
        <div className="space-y-3">
          {data.weeklyReport.aiRecommendations.map((rec, idx) => (
            <div key={idx} className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm">
              <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <p className="text-gray-700 leading-relaxed">{rec}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Weekly Summary Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Sleep */}
        <SummaryCard
          title="Somn"
          icon={Moon}
          iconColor="indigo"
          value={`${data.weeklyReport.sleep.avgDuration.toFixed(1)}h`}
          subtitle="medie/noapte"
          trend={data.weeklyReport.sleep.trend}
          insights={data.weeklyReport.sleep.insights}
        />
        
        {/* Pain */}
        <SummaryCard
          title="Durere"
          icon={Flame}
          iconColor="red"
          value={`${data.weeklyReport.pain.avgLevel.toFixed(1)}/10`}
          subtitle="nivel mediu"
          trend={data.weeklyReport.pain.trend}
          insights={data.weeklyReport.pain.insights}
        />
        
        {/* Stress */}
        <SummaryCard
          title="Stres"
          icon={Brain}
          iconColor="orange"
          value={`${data.weeklyReport.stress.avgLevel.toFixed(1)}/10`}
          subtitle="nivel mediu"
          trend={data.weeklyReport.stress.trend}
          insights={data.weeklyReport.stress.insights}
        />
        
        {/* Vitals */}
        <SummaryCard
          title="Vitale"
          icon={Activity}
          iconColor="emerald"
          value={data.weeklyReport.vitals.avgHeartRate ? `${Math.round(data.weeklyReport.vitals.avgHeartRate)} bpm` : 'N/A'}
          subtitle="puls mediu"
          trend="stable"
          insights={data.weeklyReport.vitals.insights}
        />
      </div>

      {/* Top Patterns */}
      {data.weeklyReport.topPatterns.length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-lg">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-indigo-600" />
            Top Pattern-uri Detectate
          </h3>
          <div className="space-y-3">
            {data.weeklyReport.topPatterns.map((pattern) => (
              <PatternCard key={pattern.id} pattern={pattern} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// PATTERNS TAB
// ============================================

function PatternsTab({ patterns }: { patterns: Pattern[] }) {
  if (patterns.length === 0) {
    return <EmptyState message="Nu există pattern-uri detectate încă. Continuă să adaugi logs!" />;
  }

  return (
    <div className="space-y-4">
      {patterns.map((pattern) => (
        <PatternCard key={pattern.id} pattern={pattern} expanded />
      ))}
    </div>
  );
}

// ============================================
// CORRELATIONS TAB
// ============================================

function CorrelationsTab({ correlations }: { correlations: Correlation[] }) {
  if (correlations.length === 0) {
    return <EmptyState message="Nu există corelații detectate încă." />;
  }

  const getMetricInfo = (metric: string) => {
    const map: Record<string, { label: string; icon: any; color: string }> = {
      sleep: { label: 'Somn', icon: Moon, color: 'indigo' },
      pain: { label: 'Durere', icon: Flame, color: 'red' },
      stress: { label: 'Stres', icon: Brain, color: 'orange' },
      vitals: { label: 'Vitale', icon: Heart, color: 'emerald' },
    };
    return map[metric] || { label: metric, icon: Activity, color: 'gray' };
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {correlations.map((corr, idx) => {
        const metric1Info = getMetricInfo(corr.metric1);
        const metric2Info = getMetricInfo(corr.metric2);
        const Icon1 = metric1Info.icon;
        const Icon2 = metric2Info.icon;
        
        const strengthColor = corr.strength === 'strong' ? 'green' : corr.strength === 'moderate' ? 'yellow' : 'gray';
        
        return (
          <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100 hover:border-indigo-200 transition-all">
            {/* Metrics */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`p-3 bg-${metric1Info.color}-100 rounded-xl`}>
                  <Icon1 className={`w-5 h-5 text-${metric1Info.color}-600`} />
                </div>
                <span className="font-semibold text-gray-900">{metric1Info.label}</span>
              </div>
              
              <ChevronRight className={`w-6 h-6 ${corr.direction === 'negative' ? 'rotate-180' : ''} text-gray-400`} />
              
              <div className="flex items-center gap-3">
                <span className="font-semibold text-gray-900">{metric2Info.label}</span>
                <div className={`p-3 bg-${metric2Info.color}-100 rounded-xl`}>
                  <Icon2 className={`w-5 h-5 text-${metric2Info.color}-600`} />
                </div>
              </div>
            </div>

            {/* Coefficient */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Coeficient</span>
                <span className="text-lg font-bold text-gray-900">{corr.coefficient.toFixed(2)}</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full bg-gradient-to-r ${
                    corr.direction === 'positive' ? 'from-green-400 to-emerald-500' : 'from-red-400 to-rose-500'
                  }`}
                  style={{ width: `${Math.abs(corr.coefficient) * 100}%` }}
                />
              </div>
            </div>

            {/* Strength Badge */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Putere:</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium bg-${strengthColor}-100 text-${strengthColor}-700`}>
                {corr.strength === 'strong' ? 'Puternică' : corr.strength === 'moderate' ? 'Moderată' : 'Slabă'}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// PREDICTIONS TAB
// ============================================

function PredictionsTab({ predictions, anomalies }: { predictions: Prediction[]; anomalies: Anomaly[] }) {
  return (
    <div className="space-y-6">
      {/* Anomalies */}
      {anomalies.length > 0 && (
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            Anomalii Detectate
          </h3>
          <div className="space-y-3">
            {anomalies.map((anomaly, idx) => (
              <div 
                key={idx}
                className={`bg-gradient-to-br ${
                  anomaly.severity === 'high' ? 'from-red-50 to-rose-50 border-red-300' :
                  anomaly.severity === 'medium' ? 'from-orange-50 to-amber-50 border-orange-300' :
                  'from-yellow-50 to-amber-50 border-yellow-300'
                } rounded-2xl p-6 border-2 shadow-lg`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 ${
                    anomaly.severity === 'high' ? 'bg-red-500' :
                    anomaly.severity === 'medium' ? 'bg-orange-500' :
                    'bg-yellow-500'
                  } rounded-xl`}>
                    <AlertTriangle className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-bold text-gray-900 text-lg">Anomalie: {anomaly.metric}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        anomaly.severity === 'high' ? 'bg-red-600 text-white' :
                        anomaly.severity === 'medium' ? 'bg-orange-600 text-white' :
                        'bg-yellow-600 text-white'
                      }`}>
                        {anomaly.severity === 'high' ? 'CRITIC' : anomaly.severity === 'medium' ? 'MEDIU' : 'MIC'}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-gray-600">Valoare</p>
                        <p className="text-lg font-bold text-gray-900">{anomaly.value.toFixed(1)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Așteptat</p>
                        <p className="text-lg font-bold text-gray-900">{anomaly.expectedValue.toFixed(1)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Deviere</p>
                        <p className="text-lg font-bold text-red-600">{anomaly.deviation > 0 ? '+' : ''}{anomaly.deviation.toFixed(0)}%</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      Detectat: {new Date(anomaly.date).toLocaleDateString('ro-RO')}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Predictions */}
      {predictions.length > 0 && (
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Brain className="w-6 h-6 text-purple-600" />
            Predicții AI
          </h3>
          <div className="space-y-3">
            {predictions.map((pred, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg border-2 border-purple-100">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-purple-100 rounded-xl">
                    <Target className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-gray-900 text-lg">{pred.metric}</h4>
                      <span className="text-sm text-gray-500">
                        {new Date(pred.date).toLocaleDateString('ro-RO')}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mb-3">
                      <div>
                        <p className="text-sm text-gray-600">Valoare Predicție</p>
                        <p className="text-2xl font-bold text-purple-600">{pred.predictedValue.toFixed(1)}</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Confidence</p>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-purple-400 to-purple-600"
                              style={{ width: `${pred.confidence}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-700">{pred.confidence}%</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 italic">{pred.reasoning}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {predictions.length === 0 && anomalies.length === 0 && (
        <EmptyState message="Nu există predicții sau anomalii detectate." />
      )}
    </div>
  );
}

// ============================================
// REUSABLE COMPONENTS
// ============================================

function SummaryCard({ title, icon: Icon, iconColor, value, subtitle, trend, insights }: any) {
  const trendIcon = trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→';
  const trendColor = trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-600';
  
  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100 hover:border-indigo-200 hover:shadow-xl transition-all">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 bg-${iconColor}-100 rounded-xl`}>
          <Icon className={`w-6 h-6 text-${iconColor}-600`} />
        </div>
        <span className={`text-2xl font-bold ${trendColor}`}>{trendIcon}</span>
      </div>
      <h3 className="text-gray-600 text-sm font-medium mb-1">{title}</h3>
      <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
      <p className="text-sm text-gray-500 mb-3">{subtitle}</p>
      <div className="space-y-1">
        {insights.slice(0, 2).map((insight: string, idx: number) => (
          <p key={idx} className="text-xs text-gray-600 leading-relaxed">• {insight}</p>
        ))}
      </div>
    </div>
  );
}

function PatternCard({ pattern, expanded = false }: { pattern: Pattern; expanded?: boolean }) {
  const impactColor = pattern.impact === 'positive' ? 'green' : pattern.impact === 'negative' ? 'red' : 'gray';
  
  return (
    <div className={`bg-gradient-to-br ${
      pattern.impact === 'positive' ? 'from-green-50 to-emerald-50 border-green-200' :
      pattern.impact === 'negative' ? 'from-red-50 to-rose-50 border-red-200' :
      'from-gray-50 to-slate-50 border-gray-200'
    } rounded-2xl p-6 border-2 shadow-lg hover:shadow-xl transition-all`}>
      <div className="flex items-start gap-4">
        <div className={`p-3 bg-${impactColor}-500 rounded-xl flex-shrink-0`}>
          {pattern.type === 'correlation' && <Zap className="w-6 h-6 text-white" />}
          {pattern.type === 'trend' && <TrendingUp className="w-6 h-6 text-white" />}
          {pattern.type === 'anomaly' && <AlertTriangle className="w-6 h-6 text-white" />}
        </div>
        <div className="flex-1">
          <div className="flex items-start justify-between mb-2">
            <h4 className="font-bold text-gray-900 text-lg">{pattern.title}</h4>
            <span className={`px-3 py-1 rounded-full text-xs font-medium bg-${impactColor}-600 text-white`}>
              {pattern.confidence.toFixed(0)}% Confidence
            </span>
          </div>
          <p className="text-gray-700 leading-relaxed mb-3">{pattern.description}</p>
          
          {pattern.actionable && pattern.suggestion && expanded && (
            <div className="bg-white/80 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-2">
                <Lightbulb className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-indigo-900 mb-1">Sugestie:</p>
                  <p className="text-sm text-gray-700">{pattern.suggestion}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="bg-white rounded-2xl p-12 text-center shadow-lg">
      <div className="inline-flex p-4 rounded-full bg-gray-100 mb-4">
        <BarChart3 className="w-8 h-8 text-gray-400" />
      </div>
      <p className="text-gray-600">{message}</p>
    </div>
  );
}
